import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Paper from "@mui/material/Paper";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import useUpdate from "../../../../api/useUpdate";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const PurchaseEdit = ({ purchase, onClose, token, refetchParent }) => {
  const { t } = useTranslation();
  const { data: medicines, isLoading: loadingMedicines } = useFetchObjects(
    ["medicine"],
    "medicines",
    token
  );
  const { data: companies, isLoading: loadingCompanies } = useFetchObjects(
    ["medicine-companies"],
    "medicine-companies",
    token
  );

  const [formData, setFormData] = useState({
    date: purchase?.date || new Date().toISOString().split("T")[0],
    medicine_company: purchase?.medicine_company?.id || "",
    is_approved: purchase?.is_approved || 1,
    items: purchase?.items?.map(item => ({ ...item })) || [],
    total_amount: purchase?.total_amount || 0,
    markup_percentage: purchase?.markup_percentage || 15,
  });

  // Track success from useUpdate
  const [wasSuccess, setWasSuccess] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const { handleUpdate, loading: updating, success } = useUpdate("purchases", token);

  // Calculate total amount whenever items change
  // Calculate total amount whenever items change, unless user edits manually
  useEffect(() => {
    if (!formData._manualTotalEdit) {
      const total = formData.items.reduce((sum, item) => {
        const price = parseFloat(item.purchase_price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return sum + price * quantity;
      }, 0);
      setFormData((prev) => ({ ...prev, total_amount: total }));
    }
  }, [formData.items]);

  // Always send total_amount from frontend, do not recalculate in backend

  useEffect(() => {
    if (purchase && companies) {
      // If purchase has a company, use its id, else use first company
      let defaultCompany = purchase.medicine_company?.id || (companies.length > 0 ? companies[0].id : "");
      setFormData({
        date: purchase.date,
        medicine_company: defaultCompany,
        is_approved: purchase.is_approved,
        items: purchase.items.map((item) => ({
          ...item,
          id: item.id,
          medicine: item.medicine,
          quantity: item.quantity,
          purchase_price: item.purchase_price,
          sell_price: item.sell_price,
          expiry_date: item.expiry_date,
        })),
        total_amount: purchase.total_amount || 0,
      });
    }
  }, [purchase, companies]);

  // Close dialog on success
  useEffect(() => {
    if (success && !wasSuccess) {
      setWasSuccess(true);
      setTimeout(() => {
        refetchParent();
        onClose();
      }, 500);
    }
    if (!success && wasSuccess) {
      setWasSuccess(false);
    }
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "total_amount") {
      setFormData((prev) => ({ ...prev, total_amount: parseFloat(value) || 0, _manualTotalEdit: true }));
    } else if (name === "markup_percentage") {
      // When markup changes, update all items' sell_price
      const newMarkup = parseFloat(value) || 0;
      const updatedItems = formData.items.map(item => {
        const purchasePrice = parseFloat(item.purchase_price) || 0;
        return {
          ...item,
          sell_price: purchasePrice ? Math.round(purchasePrice * (1 + newMarkup / 100)) : item.sell_price
        };
      });
      setFormData((prev) => ({ ...prev, markup_percentage: newMarkup, items: updatedItems }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [name]: value };

    // Auto-calculate sell price if purchase price changes
    if (name === "purchase_price") {
      const purchasePrice = parseFloat(value) || 0;
      const markup = parseFloat(formData.markup_percentage) || 0;
      updatedItems[index].sell_price = purchasePrice ? Math.round(purchasePrice * (1 + markup / 100)) : "";
    }

    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      date: date
        ? date.toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
    }));
  };

  // Removed handleTotalChange, now handled in handleChange

  const addNewItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          medicine: "",
          quantity: "",
          purchase_price: "",
          sell_price: "",
          expiry_date: "",
        },
      ],
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      return; // Don't allow removing the last item
    }
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    if (!formData.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }

    if (!formData.medicine_company) {
      errors.medicine_company = t("pharmacy.companyRequired");
      isValid = false;
    }

    if (formData.total_amount <= 0) {
      errors.total_amount = t("pharmacy.totalAmountRequired");
      isValid = false;
    }

    formData.items.forEach((item, index) => {
      if (!item.medicine) {
        errors[`item_${index}_medicine`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
      if (!item.purchase_price || item.purchase_price <= 0) {
        errors[`item_${index}_purchase_price`] = t(
          "pharmacy.purchasePriceRequired"
        );
        isValid = false;
      }
      if (!item.sell_price || item.sell_price <= 0) {
        errors[`item_${index}_sell_price`] = t("pharmacy.sellPriceRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Ensure medicine_company is sent as an ID, not object
      const payload = {
        ...formData,
        medicine_company: formData.medicine_company,
        total_amount: formData.total_amount, // Always send total_amount from frontend
        markup_percentage: formData.markup_percentage,
        items: formData.items.map((item) => ({
          id: item.id || undefined,
          medicine: item.medicine,
          quantity: item.quantity,
          purchase_price: item.purchase_price,
          sell_price: item.sell_price,
          expiry_date: item.expiry_date || null,
          purchase: purchase.id, // Ensure purchase field is sent for items
        })),
      };

      handleUpdate(purchase.id, payload);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2, background: '#f9f9f9', borderRadius: 2, boxShadow: 2, opacity: updating ? 0.6 : 1, pointerEvents: updating ? 'none' : 'auto', maxWidth: '1200px', margin: '0 auto' }}>
      {loadingMedicines || loadingCompanies ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
            {t("pharmacy.editPurchase", "Edit Purchase")} #{purchase?.id}
          </Typography>

          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                size="medium"
                label={t("pharmacy.markupPercentage", "Markup Percentage")}
                name="markup_percentage"
                type="number"
                value={formData.markup_percentage}
                onChange={handleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                inputProps={{ step: "0.01", min: 0, max: 100 }}
                InputLabelProps={{ shrink: true }}
                sx={{ background: '#fff', borderRadius: 2, boxShadow: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                size="medium"
                label={t("pharmacy.date", "Date")}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                error={!!formErrors.date}
                helperText={formErrors.date}
                InputLabelProps={{ shrink: true }}
                sx={{ background: '#fff', borderRadius: 2, boxShadow: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" size="medium" sx={{ background: '#fff', borderRadius: 2, boxShadow: 1 }}>
                <InputLabel>{t("pharmacy.medicineCompany", "Medicine Company")}</InputLabel>
                <Select
                  name="medicine_company"
                  value={formData.medicine_company}
                  onChange={handleChange}
                  label={t("pharmacy.medicineCompany", "Medicine Company")}
                  error={!!formErrors.medicine_company}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  {companies?.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.medicine_company && (
                  <Typography color="error" variant="caption">
                    {formErrors.medicine_company}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" size="medium" sx={{ background: '#fff', borderRadius: 2, boxShadow: 1 }}>
                <InputLabel>{t("pharmacy.status", "Status")}</InputLabel>
                <Select
                  name="is_approved"
                  value={formData.is_approved}
                  onChange={handleChange}
                  label={t("pharmacy.status", "Status")}
                >
                  <MenuItem value={1}>{t("pharmacy.pending", "Pending")}</MenuItem>
                  <MenuItem value={2}>{t("pharmacy.approved", "Approved")}</MenuItem>
                  <MenuItem value={3}>{t("pharmacy.rejected", "Rejected")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.totalAmount", "Total Amount")}
                name="total_amount"
                type="number"
                margin="normal"
                size="medium"
                value={formData.total_amount}
                onChange={handleChange}
                error={!!formErrors.total_amount}
                helperText={formErrors.total_amount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">؋</InputAdornment>
                  ),
                }}
                inputProps={{ step: "0.01", min: 0 }}
                sx={{ background: '#fff', borderRadius: 2, boxShadow: 1 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: 'primary.main', borderWidth: 2 }} />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ px: 1 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: 0.5 }}>{t("pharmacy.medicines", "Medicines")}</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addNewItem}
              variant="contained"
              size="medium"
              sx={{ borderRadius: 2, fontWeight: 600, boxShadow: 2, textTransform: 'none' }}
            >
              {t("pharmacy.addMedicine", "Add Medicine")}
            </Button>
          </Box>

          {formData.items.map((item, index) => (
            <Paper key={index} sx={{ p: 3, mb: 3, position: "relative", borderRadius: 3, boxShadow: 3, background: '#fff' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <TextField
                    select
                    fullWidth
                    label={t("pharmacy.medicine", "Medicine")}
                    name="medicine"
                    size="medium"
                    value={item.medicine}
                    onChange={(e) => handleItemChange(index, e)}
                    error={!!formErrors[`item_${index}_medicine`]}
                    helperText={formErrors[`item_${index}_medicine`]}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  >
                    {medicines?.map((medicine) => (
                      <MenuItem key={medicine.id} value={medicine.id}>
                        {medicine.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label={t("pharmacy.quantity", "Quantity")}
                    name="quantity"
                    type="number"
                    size="medium"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, e)}
                    error={!!formErrors[`item_${index}_quantity`]}
                    helperText={formErrors[`item_${index}_quantity`]}
                    inputProps={{ min: 1 }}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label={t("pharmacy.purchasePrice", "Purchase Price")}
                    name="purchase_price"
                    type="number"
                    size="medium"
                    value={item.purchase_price}
                    onChange={(e) => handleItemChange(index, e)}
                    error={!!formErrors[`item_${index}_purchase_price`]}
                    helperText={formErrors[`item_${index}_purchase_price`]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">؋</InputAdornment>
                      ),
                    }}
                    inputProps={{ step: "0.01", min: 0.01 }}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label={t("pharmacy.sellPrice", "Sell Price")}
                    name="sell_price"
                    type="number"
                    size="medium"
                    value={item.sell_price}
                    onChange={(e) => handleItemChange(index, e)}
                    error={!!formErrors[`item_${index}_sell_price`]}
                    helperText={formErrors[`item_${index}_sell_price`]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">؋</InputAdornment>
                      ),
                    }}
                    inputProps={{ step: "0.01", min: 0.01 }}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  />
                </Grid>
                <Grid item xs={5} md={2}>
                  <TextField
                    fullWidth
                    name="expiry_date"
                    type="date"
                    label={t("pharmacy.expiryDate", "Expiry Date")}
                    size="medium"
                    InputLabelProps={{ shrink: true }}
                    value={item.expiry_date || ""}
                    onChange={(e) => handleItemChange(index, e)}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  />
                </Grid>
                <Grid
                  item
                  xs={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconButton
                    color="error"
                    onClick={() => removeItem(index)}
                    disabled={formData.items.length <= 1}
                    sx={{ borderRadius: 2, boxShadow: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {formData.items.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontWeight: 600, fontSize: 16 }}>
              {t("pharmacy.noMedicinesWarning", "No medicines added. Please add at least one medicine.")}
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              onClick={onClose}
              variant="outlined"
              size="large"
              disabled={updating}
              sx={{ minWidth: 120, borderRadius: 2, fontWeight: 600, fontSize: 16, boxShadow: 1 }}
            >
              {t("pharmacy.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              disabled={updating || formData.items.length === 0}
              sx={{ minWidth: 120, borderRadius: 2, fontWeight: 600, fontSize: 16, boxShadow: 2 }}
            >
              {updating ? <CircularProgress size={24} /> : t("pharmacy.save", "Save")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PurchaseEdit;
